import { useCallback, useRef, useState } from 'react';

/**
 * Tiny WebAudio sound engine for the games. Synthesised on the fly (no audio
 * assets shipped) so it adds zero bytes to the consumer bundle.
 *
 * Muted by default — audio at a sponsored-event kiosk should be opt-in. The
 * `<GameStage>` renders a speaker toggle wired to `toggle()`. The AudioContext
 * is created lazily on the first enable (a user gesture) to satisfy browser
 * autoplay policies.
 */
export type GameSound = 'tick' | 'win' | 'reveal' | 'kick' | 'spinUp';

export interface SoundController {
  enabled: boolean;
  toggle: () => void;
  play: (sound: GameSound) => void;
}

export function useGameSound(): SoundController {
  const [enabled, setEnabled] = useState(false);
  const ctxRef = useRef<AudioContext | null>(null);

  const ensureCtx = useCallback((): AudioContext | null => {
    if (typeof window === 'undefined') return null;
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return null;
    if (!ctxRef.current) ctxRef.current = new AC();
    if (ctxRef.current.state === 'suspended') void ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const tone = useCallback(
    (
      ctx: AudioContext,
      freq: number,
      start: number,
      dur: number,
      type: OscillatorType = 'sine',
      peak = 0.16,
    ) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(peak, ctx.currentTime + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        ctx.currentTime + start + dur,
      );
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.02);
    },
    [],
  );

  const play = useCallback(
    (sound: GameSound) => {
      if (!enabled) return;
      const ctx = ensureCtx();
      if (!ctx) return;
      switch (sound) {
        case 'tick':
          tone(ctx, 1200, 0, 0.04, 'square', 0.08);
          break;
        case 'spinUp':
          tone(ctx, 220, 0, 0.18, 'sawtooth', 0.07);
          tone(ctx, 440, 0.05, 0.16, 'sawtooth', 0.05);
          break;
        case 'reveal':
          tone(ctx, 660, 0, 0.12, 'sine', 0.12);
          tone(ctx, 990, 0.08, 0.16, 'sine', 0.1);
          break;
        case 'kick':
          tone(ctx, 160, 0, 0.16, 'triangle', 0.2);
          break;
        case 'win':
          [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
            tone(ctx, f, i * 0.1, 0.22, 'triangle', 0.14),
          );
          break;
      }
    },
    [enabled, ensureCtx, tone],
  );

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      if (next) {
        const ctx = ensureCtx();
        // tiny confirmation blip so the toggle feels responsive
        if (ctx) tone(ctx, 880, 0, 0.06, 'sine', 0.1);
      }
      return next;
    });
  }, [ensureCtx, tone]);

  return { enabled, toggle, play };
}
